var _ = require('underscore');
// ViewModelBase adds a little bit of structure to the process of defining a
// view model for knockout and also provides a mechanism for defining modules
// of standalone behavior that can be included into a new view model. This
// makes sharing common sets of properties, observables, dependent observables,
// and methods across multiple view models possible without tightly coupling
// those view models together.
//
// New modules should be defined in tk/view_model/name.js and then included in
// any JS bundles with view models that require that module.
//
// Creating a new view model with ViewModelBase.create returns a constructor
// function with all of the shared methods from modules and the one-off methods
// added to the prototype, so that instances of the view model will have all
// of the appropriate methods.
//
// When a class created by ViewModelBase.create is instantiated with the new
// keyword, the constructor calls the init methods of the included modules in
// the order that they're included with no arguments. Finally, it calls the
// provided init method with the arguments given to the constructor.
//
// Example:
//
// // Defined in tk/view_model/family_info.js
// ViewModelBase.modules.FamilyInfo = {
//   init: function() {
//     // Initialize common properties, observables, dependent observables.
//   },
//   // Define common methods...
// };
//
// // Defined in namespace/example.js
// Example = ViewModelBase.create({
//   include: ['FamilyInfo', 'FamilySample'],
//   init: function(arg1, arg2, arg3) {
//     // Initialize one-off properties, observables, dependent observables.
//   },
//   // Most view model objects should implement update, but it's not special.
//   update: function(data) {
//     ViewModelBase.update(this, data, {
//       'observableName': 'key',
//       'propertyName': 'nested.other-key'
//     });
//   },
//   // Define other one-off methods...
// });
//
// // Defined in a template's view model init block
// var viewModelInstance = new Example(arg1, arg2, arg3);
// viewModelInstance.update({
//   'key': 'value',
//   'nested': {
//     'other-key': 'other-value'
//   }
// });
//
// viewModelInstance.observableName(); // => 'value'
// viewModelInstance.propertyName; // => 'other-value'
//

var ViewModelBase = {
  // Create a new view model constructor using the given options and an optional
  // set of modules. If no set of modules is given, the default set is used.
  //
  // options - An Object describing the class that should be created:
  //           'include' - An Array of String module names to include in the
  //                       created view model class (default: none).
  //           'init'    - An initialization Function to call when instantiating
  //                       the created view model class (default: none).
  //           '*'       - Any other properties of this objects should be
  //                       Functions with String keys, and will be made available
  //                       as instance methods on the view model class.
  // methods - An Object of module Objects to use (default: ViewModelBase.modules).
  //
  // Returns a Function constructor for this view model class.
  create: function(options, modules) {
    if (!modules) modules = ViewModelBase.modules;

    // Create the constructor function.
    var viewModelClass = function() {
      _.each(options.include, function(moduleName) {
        var module = modules[moduleName];
        if (module && module.init) module.init.apply(this);
      }, this);
      if (options.init) options.init.apply(this, arguments);
    };

    // Include module methods and one-off methods on the prototype.
    _.each(options.include, function(moduleName) {
      var module = modules[moduleName];
      if (module) _.extend(viewModelClass.prototype, module);
    });
    _.extend(viewModelClass.prototype, options);

    // Remove the special 'include' and 'init' keywords from the prototype.
    delete viewModelClass.prototype.include;
    delete viewModelClass.prototype.init;
    return viewModelClass;
  },

  // A helper for use in view model update methods.
  //
  // Uses a mapping of property names to data object keys to assign values from
  // a given data object to properties and observables of the given view model
  // instance.
  //
  // The keys of the mapping should be String property names of the view model
  // instance. The values should be String paths in the data object (with
  // periods marking nesting levels).
  //
  // If the resulting data value is undefined, it will be skipped and the
  // current value of the property will be left as-is.
  //
  // If the property of the view model instance identified by the name is a
  // Function, it will be called with the resulting data value. Otherwise, the
  // resulting data value will be assigned to the property.
  //
  // viewModelInstance - An Object instance of a view model class.
  // data              - An Object of nested keys and values, like from an API's
  //                     JSON response.
  // mapping           - An Object mapping property names to data keys or paths.
  //
  // Returns nothing.
  update: function(viewModelInstance, data, mapping) {
    _.each(mapping, function(dataName, propName) {
      var value = _.reduce(dataName.split('.'), function(d, k) { return _.isUndefined(d) ? undefined : d[k]; }, data);
      if (!_.isUndefined(value)) {
        if(_.isFunction(viewModelInstance[propName])) {
          viewModelInstance[propName](value);
        } else {
          viewModelInstance[propName] = value;
        }
      }
    });
  },

  // The default set of modules for inclusion in the view models that .create
  // makes.
  modules: {}
};

module.exports = ViewModelBase;
